import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const flowSteps = [
  { label: '课堂原话', icon: 'ri-mic-line' },
  { label: '案例实体识别', icon: 'ri-scan-line' },
  { label: '权威公开资料', icon: 'ri-global-line' },
  { label: '关键数据核验', icon: 'ri-check-double-line' },
  { label: '讲稿备注', icon: 'ri-sticky-note-line' },
  { label: '课件资源', icon: 'ri-slideshow-3-line' },
  { label: '集体备课案例库', icon: 'ri-folder-open-line' },
];

export default function P41CaseHighSpeedRail({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100/60 text-accent-700 text-[11px] font-body font-semibold">
              <i className="ri-file-text-line text-xs" /> 完整闭环案例 B
            </span>
          </div>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100">
            从<span className="text-primary-600">"京沪高铁"</span>到权威资料与讲稿备注
          </h2>
        </div>

        {/* 流程 */}
        <div className="max-w-5xl mx-auto mb-5 transition-all duration-1000 delay-200">
          <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-2">
            {flowSteps.map((step, i) => (
              <div key={step.label} className="flex items-center gap-0">
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl glass-liquid border-primary-200/30 transition-all duration-300 hover:scale-105">
                  <span className="font-mono text-[10px] font-bold text-primary-400">{i + 1}</span>
                  <i className={`${step.icon} text-sm text-primary-500`} />
                  <span className="text-xs font-body font-semibold text-foreground-700 whitespace-nowrap">{step.label}</span>
                </div>
                {i < flowSteps.length - 1 && (
                  <i className="ri-arrow-right-s-line text-foreground-300 mx-0.5 text-sm" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 说明 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-5 transition-all duration-1000 delay-400">
          <div className="p-4 rounded-2xl glass-liquid border-primary-200/30">
            <div className="relative z-10">
              <i className="ri-search-line text-lg text-primary-500 mb-2" />
              <h3 className="font-heading text-sm font-bold text-foreground-800 mb-1">资料关联</h3>
              <p className="text-[11px] text-foreground-500 font-body leading-relaxed">
                识别案例实体、关键数字和理论命题后，连接官方公开资料核对里程、建设时间和运营节点，整理为讲稿备注
              </p>
            </div>
          </div>
          <div className="p-4 rounded-2xl glass-liquid-gold border-accent-200/30">
            <div className="relative z-10">
              <i className="ri-archive-line text-lg text-accent-500 mb-2" />
              <h3 className="font-heading text-sm font-bold text-foreground-800 mb-1">教学功能判断</h3>
              <p className="text-[11px] text-foreground-500 font-body leading-relaxed">
                京沪高铁用于说明发展成就、制度优势还是科技创新？案例前有没有问题，案例后有没有理论解释？完成这些关系，资料才真正进入教学
              </p>
            </div>
          </div>
          <div className="p-4 rounded-2xl glass-liquid border-primary-200/30">
            <div className="relative z-10">
              <i className="ri-stack-line text-lg text-primary-500 mb-2" />
              <h3 className="font-heading text-sm font-bold text-foreground-800 mb-1">教师收获</h3>
              <p className="text-[11px] text-foreground-500 font-body leading-relaxed">
                一张案例卡：课堂原话、关联资料、建议备注、适用理论和可复用方式。案例有出处、能解释、可进入教研资源
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto text-center transition-all duration-1000 delay-900">
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl glass-gold border-accent-200/30">
            <i className="ri-lightbulb-line text-accent-500 text-sm" />
            <span className="text-xs font-body text-foreground-500">
              目标不是发现错误，而是让优秀案例变得可追溯、可复用
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}